import {
  findWeightBandByKg,
  weightBandCost,
  weightChargeForKg,
} from "./adminEditBooking";

export const WALK_IN_CREATE_FOR = ["None", "Sender", "Receiver", "Both"];
export const WALK_IN_ACCOUNT_MODES = ["None", "New", "Existing"];
export const WALK_IN_OWNERS = ["Sender", "Receiver"];

export const emptyWalkInFields = {
  walkInCreateAccountsFor: "None",
  walkInBookingOwner: "Sender",
  walkInSenderMode: "None",
  walkInSenderExistingUserId: "",
  walkInSenderExistingLabel: "",
  walkInSenderUserName: "",
  walkInSenderEmail: "",
  walkInSenderPassword: "",
  walkInReceiverMode: "None",
  walkInReceiverExistingUserId: "",
  walkInReceiverExistingLabel: "",
  walkInReceiverUserName: "",
  walkInReceiverEmail: "",
  walkInReceiverPassword: "",
  walkInFinalPrice: "",
  walkInFinalPriceEdited: false,
  walkInCalculatedPrice: "",
};

export function slugifyUserName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .slice(0, 32);
}

export function generateWalkInPassword(length = 10) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function partyEnabled(createFor, party) {
  if (createFor === "Both") return true;
  return createFor === party;
}

/**
 * @param {object} data
 * @returns {string|null}
 */
export function validateWalkInAccounts(data = {}) {
  const createFor = data.walkInCreateAccountsFor || "None";
  const owner = data.walkInBookingOwner || "Sender";

  if (!WALK_IN_OWNERS.includes(owner)) {
    return "Choose whether the sender or receiver owns this booking.";
  }

  const checkParty = (party) => {
    const enabled = partyEnabled(createFor, party);
    const prefix = party === "Sender" ? "walkInSender" : "walkInReceiver";
    const mode = data[`${prefix}Mode`] || "None";
    if (!enabled) return null;
    if (mode === "None") {
      return `Choose New or Existing for the ${party.toLowerCase()} account.`;
    }
    if (mode === "Existing" && !data[`${prefix}ExistingUserId`]) {
      return `Select an existing ${party.toLowerCase()} user.`;
    }
    if (mode === "New") {
      if (!String(data[`${prefix}UserName`] || "").trim()) {
        return `Username is required for the new ${party.toLowerCase()} account.`;
      }
      if (!String(data[`${prefix}Password`] || "").trim()) {
        return `Password is required for the new ${party.toLowerCase()} account.`;
      }
    }
    return null;
  };

  return checkParty("Sender") || checkParty("Receiver");
}

function accountPayload(data, party) {
  const createFor = data.walkInCreateAccountsFor || "None";
  const prefix = party === "Sender" ? "walkInSender" : "walkInReceiver";
  const enabled = partyEnabled(createFor, party);
  const mode = enabled ? data[`${prefix}Mode`] || "None" : "None";
  return {
    mode,
    existingUserId:
      mode === "Existing" ? data[`${prefix}ExistingUserId`] || null : null,
    userName: mode === "New" ? String(data[`${prefix}UserName`] || "").trim() : "",
    email: mode === "New" ? String(data[`${prefix}Email`] || "").trim() : "",
    password: mode === "New" ? String(data[`${prefix}Password`] || "") : "",
  };
}

/**
 * Flattened account fields the AdminBooking/Create endpoint expects,
 * plus nested objects for backends that unwrap them.
 */
export function buildWalkInAccountPayload(data = {}) {
  const sender = accountPayload(data, "Sender");
  const receiver = accountPayload(data, "Receiver");
  return {
    isWalkIn: true,
    createAccountsFor: data.walkInCreateAccountsFor || "None",
    bookingOwner: data.walkInBookingOwner || "Sender",
    senderAccountMode: sender.mode,
    senderExistingUserId: sender.existingUserId,
    senderUserName: sender.userName,
    senderEmail: sender.email,
    senderPassword: sender.password,
    receiverAccountMode: receiver.mode,
    receiverExistingUserId: receiver.existingUserId,
    receiverUserName: receiver.userName,
    receiverEmail: receiver.email,
    receiverPassword: receiver.password,
    senderAccount: sender,
    receiverAccount: receiver,
  };
}

function requiredText(value, fallback = "N/A") {
  const text = value == null ? "" : String(value).trim();
  return text || fallback;
}

function requiredDateTime(value, fallback = "2024-07-24T00:00:00") {
  const text = value == null ? "" : String(value).trim();
  if (!text) return fallback;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T00:00:00`;
  return text;
}

const REQUIRED_BOOKING_TEXT_FIELDS = [
  "senderName",
  "senderEmail",
  "senderMobileNo",
  "senderLandMark",
  "senderPostCode",
  "senderAdditionalAddressInfo",
  "senderDoorNo",
  "senderStreetName",
  "senderRemarks",
  "receiverName",
  "receiverEmail",
  "receiverMobileNo",
  "receiverLandMark",
  "receiverPostCode",
  "receiverAdditionalAddressInfo",
  "receiverDoorNo",
  "receiverStreetName",
  "receiverRemarks",
  "pickUpLandMark",
  "pickUpAdditionalAddressInfo",
  "parcelPickUpInstractions",
  "bookedById",
];

/**
 * AdminBooking/Create binds a nested `Booking` object (ASP.NET: Booking.SenderName, …).
 * Root-level booking fields are ignored and produce 400 "field is required" errors.
 */
export function buildAdminWalkInCreateBody(bookingData = {}, accountPayload = {}) {
  const booking = { ...bookingData };
  for (const key of REQUIRED_BOOKING_TEXT_FIELDS) {
    booking[key] = requiredText(booking[key]);
  }
  booking.fromTime = requiredDateTime(booking.fromTime);
  booking.toTime = requiredDateTime(booking.toTime);
  booking.products = Array.isArray(booking.products) ? booking.products : [];
  if (!booking.bookedById || booking.bookedById === "N/A") {
    booking.bookedById = requiredText(
      booking.creatorId || booking.modifierId,
      requiredText(booking.bookedById),
    );
  }

  return {
    booking,
    Booking: booking,
    ...accountPayload,
  };
}

export function publicTrackPath(token) {
  const value = String(token || "").trim();
  if (!value) return "";
  return `/track/${encodeURIComponent(value)}`;
}

export function publicTrackUrl(token) {
  const path = publicTrackPath(token);
  if (!path || typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export function itemWeightKg(item = {}, fallbackKg = 0) {
  const raw =
    item?.itemWeight !== undefined && item?.itemWeight !== ""
      ? item.itemWeight
      : fallbackKg;
  const kg = parseFloat(raw);
  return Number.isFinite(kg) && kg > 0 ? kg : 0;
}

export function walkInGrossFromTransit(
  transitCost,
  totalCost,
  vatAmountPercentage,
) {
  if (Array.isArray(transitCost) && transitCost.length) {
    return transitCost.reduce((sum, tc) => {
      const withVat = parseFloat(tc?.totalCostWithVAT);
      if (Number.isFinite(withVat) && withVat > 0) return sum + withVat;
      const net =
        parseFloat(tc?.totalCostWithoutVAT ?? tc?.totalCost ?? 0) || 0;
      const vat =
        parseFloat(tc?.vatPercentageAmount ?? tc?.vatAmount ?? 0) || 0;
      return sum + net + vat;
    }, 0);
  }
  return parseFloat(totalCost || 0) + parseFloat(vatAmountPercentage || 0);
}

export function resolveWalkInWeightRate({
  kg,
  routeId,
  weights = [],
  transitWeightCost,
  deliveryPlanId,
} = {}) {
  const band = findWeightBandByKg(kg, weights, routeId, deliveryPlanId);
  const perKg = weightBandCost(band);
  const chargeFromBand = weightChargeForKg(kg, perKg);
  if (perKg != null) {
    return { kg, perKg, charge: chargeFromBand, band };
  }
  const transit = parseFloat(transitWeightCost);
  const fallbackPerKg =
    kg > 0 && Number.isFinite(transit) && transit > 0
      ? Number((transit / kg).toFixed(4))
      : null;
  return {
    kg,
    perKg: fallbackPerKg,
    charge: Number.isFinite(transit) && transit > 0 ? transit : null,
    band: null,
  };
}

export function walkInItemWeightRates({
  products = [],
  fallbackKg,
  routeId,
  weights = [],
  transitCost = [],
} = {}) {
  const rows = Array.isArray(products) ? products : [];
  const source = rows.length ? rows : [{}];
  return source.map((item, idx) => {
    const kg = itemWeightKg(item, idx === 0 ? fallbackKg : 0);
    const tc = Array.isArray(transitCost)
      ? transitCost[idx] || transitCost[0] || {}
      : {};
    return {
      index: idx,
      label:
        item?.itemDescription ||
        item?.productTypeName ||
        item?.productGroupName ||
        `Item ${idx + 1}`,
      qty: item?.productQty || 1,
      ...resolveWalkInWeightRate({
        kg,
        routeId,
        weights,
        transitWeightCost: tc.weightCost ?? tc.WeightCost,
        deliveryPlanId: item?.deliveryPlanId,
      }),
    };
  });
}

export function applyPayableOverrideToTransitCost(transitCost, payable) {
  const rows = Array.isArray(transitCost)
    ? transitCost.map((row) => ({ ...row }))
    : [];
  if (!payable?.overridden || !(Number(payable.gross) > 0)) return rows;

  const gross = Number(payable.gross) || 0;
  const net = Number(payable.net) || 0;
  const vat = Number(payable.vat) || 0;

  if (rows.length <= 1) {
    const base = rows[0] || {};
    return [
      {
        ...base,
        totalCostWithoutVAT: net,
        afterDiscountCost: net,
        vatPercentageAmount: vat,
        vatAmount: vat,
        totalCostWithVAT: gross,
        totalCost: gross,
      },
    ];
  }

  const currentGross = walkInGrossFromTransit(rows, 0, 0) || gross;
  const scale = currentGross > 0 ? gross / currentGross : 1;
  let allocatedGross = 0;
  return rows.map((row, idx) => {
    const rowNet = parseFloat(row.totalCostWithoutVAT ?? row.totalCost ?? 0) || 0;
    const rowVat = parseFloat(row.vatPercentageAmount ?? row.vatAmount ?? 0) || 0;
    const rowGross =
      parseFloat(row.totalCostWithVAT) ||
      (rowNet > 0 || rowVat > 0 ? rowNet + rowVat : 0);
    const nextGross =
      idx === rows.length - 1
        ? Number((gross - allocatedGross).toFixed(2))
        : Number((rowGross * scale).toFixed(2));
    allocatedGross += nextGross;
    const vatRate = rowNet > 0 ? rowVat / rowNet : net > 0 ? vat / net : 0;
    const nextNet =
      vatRate > 0
        ? Number((nextGross / (1 + vatRate)).toFixed(2))
        : nextGross;
    const nextVat = Number((nextGross - nextNet).toFixed(2));
    return {
      ...row,
      totalCostWithoutVAT: nextNet,
      afterDiscountCost: nextNet,
      vatPercentageAmount: nextVat,
      vatAmount: nextVat,
      totalCostWithVAT: nextGross,
      totalCost: nextGross,
    };
  });
}

export function walkInSuggestedGross({
  calculatedGross = 0,
  weightRates = [],
} = {}) {
  const fromTransit = Number(calculatedGross) || 0;
  if (fromTransit > 0) return Number(fromTransit.toFixed(2));
  const fromWeight = (Array.isArray(weightRates) ? weightRates : []).reduce(
    (sum, row) => sum + (Number(row?.charge) || 0),
    0,
  );
  return fromWeight > 0 ? Number(fromWeight.toFixed(2)) : 0;
}

export function applyWalkInFinalPrice(
  calculatedGross,
  calculatedNet,
  calculatedVat,
  overrideRaw,
) {
  const calculated = Number(calculatedGross) || 0;
  const override = parseFloat(overrideRaw);
  const hasOverride = Number.isFinite(override) && override > 0;
  if (!hasOverride || Math.abs(override - calculated) < 0.005) {
    return {
      gross: calculated,
      net: Number(calculatedNet) || 0,
      vat: Number(calculatedVat) || 0,
      overridden: false,
    };
  }
  const net0 = Number(calculatedNet) || 0;
  const vat0 = Number(calculatedVat) || 0;
  const vatRate = net0 > 0 ? vat0 / net0 : 0;
  const gross = Number(override.toFixed(2));
  const net =
    vatRate > 0 ? Number((gross / (1 + vatRate)).toFixed(2)) : gross;
  const vat = Number((gross - net).toFixed(2));
  return { gross, net, vat, overridden: true };
}

/** One public token for the parcel (prefer shared, else sender, else receiver). */
export function pickWalkInTrackingToken(...sources) {
  for (const src of sources) {
    if (src == null) continue;
    if (typeof src === "string" && src.trim()) return src.trim();
    if (typeof src !== "object") continue;
    const token =
      src.trackingToken ||
      src.TrackingToken ||
      src.publicTrackingToken ||
      src.PublicTrackingToken ||
      src.senderTrackingToken ||
      src.SenderTrackingToken ||
      src.receiverTrackingToken ||
      src.ReceiverTrackingToken;
    if (token && String(token).trim()) return String(token).trim();
  }
  return "";
}
